import { EdgeTypeSummary, GraphData, GraphLink, GraphNode, NodeTypeSummary } from '../types';

export type ScentEntity = 'nodes' | 'edges';
export type ScentAggregate = 'count' | 'runtime' | 'runtime_x1000' | 'fanout' | 'sum' | 'avg' | 'min' | 'max';

export type ScentControls = {
  groupBy: string;
  aggregate: ScentAggregate;
  attribute: string | null;
};

export type ScentControlState = {
  nodes: ScentControls;
  edges: ScentControls;
};

export type ScentMetricOption = {
  id: string;
  label: string;
  aggregate: ScentAggregate;
  attribute: string | null;
};

type ContinuationSummary = (NodeTypeSummary | EdgeTypeSummary) & {
  value: number;
  metricValue: number;
  metricLabel: string;
  groupBy: string;
  canNavigate: boolean;
};

const NODE_TYPE_KEY = 'Node Type';
const EDGE_TYPE_KEY = 'Edge Type';
const MISSING_VALUE = '(missing)';

const NODE_INTERNAL_KEYS = new Set(['id']);
const EDGE_INTERNAL_KEYS = new Set(['source', 'target', 'key']);
const NODE_BUILTIN_KEYS = ['neighbor_count', 'in_degree', 'out_degree'];
const EDGE_BUILTIN_KEYS = ['source_neighbor_count', 'target_neighbor_count', 'endpoint_neighbor_sum'];

const getTypeKey = (entity: ScentEntity) => entity === 'nodes' ? NODE_TYPE_KEY : EDGE_TYPE_KEY;

const getInternalKeys = (entity: ScentEntity) => entity === 'nodes' ? NODE_INTERNAL_KEYS : EDGE_INTERNAL_KEYS;

const getEndpointId = (endpoint: unknown): string => {
  if (endpoint && typeof endpoint === 'object' && 'id' in endpoint) {
    return String((endpoint as { id: string | number }).id);
  }
  return String(endpoint);
};

const isNumericValue = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

const getGroupValue = (item: GraphNode | GraphLink, groupBy: string): string => {
  const rawValue = (item as any)[groupBy];
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return MISSING_VALUE;
  }
  return String(rawValue);
};

export const defaultScentControls: ScentControlState = {
  nodes: { groupBy: NODE_TYPE_KEY, aggregate: 'count', attribute: null },
  edges: { groupBy: EDGE_TYPE_KEY, aggregate: 'count', attribute: null }
};

export const encodeScentMetric = (controls: Pick<ScentControls, 'aggregate' | 'attribute'>): string => {
  if (controls.aggregate === 'count') {
    return 'count';
  }
  if (controls.aggregate === 'runtime') {
    return 'runtime';
  }
  if (controls.aggregate === 'runtime_x1000') {
    return 'runtime_x1000';
  }
  if (controls.aggregate === 'fanout') {
    return 'fanout';
  }
  return `${controls.aggregate}:${controls.attribute || ''}`;
};

export const decodeScentMetric = (id: string): Pick<ScentControls, 'aggregate' | 'attribute'> => {
  if (id === 'count') {
    return { aggregate: 'count', attribute: null };
  }
  if (id === 'runtime') {
    return { aggregate: 'runtime', attribute: null };
  }
  if (id === 'runtime_x1000') {
    return { aggregate: 'runtime_x1000', attribute: null };
  }
  if (id === 'fanout') {
    return { aggregate: 'fanout', attribute: null };
  }
  const [aggregate, attribute] = id.split(':');
  const validAggregate = ['sum', 'avg', 'min', 'max'].includes(aggregate)
    ? aggregate as ScentAggregate
    : 'count';
  return validAggregate === 'count'
    ? { aggregate: 'count', attribute: null }
    : { aggregate: validAggregate, attribute: attribute || null };
};

export const withScentAttributes = (graphData: GraphData): GraphData => {
  const nodeIds = new Set(graphData.nodes.map(node => String(node.id)));
  const inDegree = new Map<string, number>();
  const outDegree = new Map<string, number>();
  const neighbors = new Map<string, Set<string>>();

  graphData.nodes.forEach(node => {
    const nodeId = String(node.id);
    inDegree.set(nodeId, 0);
    outDegree.set(nodeId, 0);
    neighbors.set(nodeId, new Set());
  });

  graphData.links.forEach(link => {
    const sourceId = getEndpointId(link.source);
    const targetId = getEndpointId(link.target);

    if (nodeIds.has(sourceId)) {
      outDegree.set(sourceId, (outDegree.get(sourceId) || 0) + 1);
      neighbors.get(sourceId)?.add(targetId);
    }

    if (nodeIds.has(targetId)) {
      inDegree.set(targetId, (inDegree.get(targetId) || 0) + 1);
      neighbors.get(targetId)?.add(sourceId);
    }
  });

  const nodes = graphData.nodes.map(node => {
    const nodeId = String(node.id);
    return {
      ...node,
      neighbor_count: neighbors.get(nodeId)?.size || 0,
      in_degree: inDegree.get(nodeId) || 0,
      out_degree: outDegree.get(nodeId) || 0
    };
  });

  const nodeMetrics = new Map(nodes.map(node => [String(node.id), node]));

  const links = graphData.links.map(link => {
    const sourceId = getEndpointId(link.source);
    const targetId = getEndpointId(link.target);
    const sourceNeighborCount = Number(nodeMetrics.get(sourceId)?.neighbor_count || 0);
    const targetNeighborCount = Number(nodeMetrics.get(targetId)?.neighbor_count || 0);

    return {
      ...link,
      source_neighbor_count: sourceNeighborCount,
      target_neighbor_count: targetNeighborCount,
      endpoint_neighbor_sum: sourceNeighborCount + targetNeighborCount
    };
  });

  return { nodes, links };
};

export const getScentGroupByOptions = (
  items: Array<GraphNode | GraphLink>,
  entity: ScentEntity
): string[] => {
  const typeKey = getTypeKey(entity);
  const internalKeys = getInternalKeys(entity);
  const candidates = new Map<string, Set<string>>();

  items.forEach(item => {
    Object.entries(item as any).forEach(([key, value]) => {
      if (key === typeKey || internalKeys.has(key)) {
        return;
      }
      if (value === null || value === undefined || value === '') {
        return;
      }
      if (isNumericValue(value)) {
        return;
      }
      if (!candidates.has(key)) {
        candidates.set(key, new Set());
      }
      candidates.get(key)!.add(String(value));
    });
  });

  const attrs = Array.from(candidates.entries())
    .filter(([, values]) => values.size > 1 && values.size <= 50)
    .map(([key]) => key)
    .sort((a, b) => a.localeCompare(b));

  return [typeKey, ...attrs];
};

export const getScentMetricOptions = (
  items: Array<GraphNode | GraphLink>,
  entity: ScentEntity
): ScentMetricOption[] => {
  const typeKey = getTypeKey(entity);
  const internalKeys = getInternalKeys(entity);
  const builtinKeys = entity === 'nodes' ? NODE_BUILTIN_KEYS : EDGE_BUILTIN_KEYS;
  const numericKeys = new Set<string>();

  items.forEach(item => {
    Object.entries(item as any).forEach(([key, value]) => {
      if (key === typeKey || internalKeys.has(key)) {
        return;
      }
      if (isNumericValue(value)) {
        numericKeys.add(key);
      }
    });
  });

  const orderedKeys = [
    ...builtinKeys.filter(key => numericKeys.has(key)),
    ...Array.from(numericKeys)
      .filter(key => !builtinKeys.includes(key))
      .sort((a, b) => a.localeCompare(b))
  ];

  const options: ScentMetricOption[] = [
    { id: 'count', label: 'Count', aggregate: 'count', attribute: null },
    { id: 'runtime', label: 'Expected compute time', aggregate: 'runtime', attribute: null },
    { id: 'runtime_x1000', label: 'Expected compute time x1000', aggregate: 'runtime_x1000', attribute: null },
    { id: 'fanout', label: 'Avg fanout', aggregate: 'fanout', attribute: null }
  ];

  orderedKeys.forEach(attribute => {
    options.push(
      { id: `avg:${attribute}`, label: `Avg ${attribute}`, aggregate: 'avg', attribute },
      { id: `sum:${attribute}`, label: `Sum ${attribute}`, aggregate: 'sum', attribute },
      { id: `min:${attribute}`, label: `Min ${attribute}`, aggregate: 'min', attribute },
      { id: `max:${attribute}`, label: `Max ${attribute}`, aggregate: 'max', attribute }
    );
  });

  return options;
};

export const resolveScentControls = (
  controls: ScentControls,
  metricOptions: ScentMetricOption[],
  groupByOptions: string[],
  entity: ScentEntity
): ScentControls => {
  const defaultControls = defaultScentControls[entity];
  const metricId = encodeScentMetric(controls);
  const metricExists = metricOptions.some(option => option.id === metricId);

  return {
    groupBy: groupByOptions.includes(controls.groupBy) ? controls.groupBy : defaultControls.groupBy,
    aggregate: metricExists ? controls.aggregate : 'count',
    attribute: metricExists ? controls.attribute : null
  };
};

const aggregateValues = (values: number[], controls: ScentControls): number => {
  if (controls.aggregate === 'count') {
    return values.length;
  }
  if (controls.aggregate === 'runtime') {
    return values.length;
  }
  if (controls.aggregate === 'runtime_x1000') {
    return values.length;
  }
  if (controls.aggregate === 'fanout') {
    return values.length;
  }
  if (values.length === 0) {
    return 0;
  }
  switch (controls.aggregate) {
    case 'sum':
      return values.reduce((sum, value) => sum + value, 0);
    case 'avg':
      return values.reduce((sum, value) => sum + value, 0) / values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    default:
      return values.length;
  }
};

const formatMetricLabel = (metricValue: number, controls: ScentControls): string => {
  if (controls.aggregate === 'count') {
    return `${metricValue.toLocaleString()} count`;
  }
  if (controls.aggregate === 'runtime') {
    return 'Expected compute time';
  }
  if (controls.aggregate === 'runtime_x1000') {
    return 'Expected compute time x1000';
  }
  if (controls.aggregate === 'fanout') {
    return 'Avg fanout';
  }
  const rounded = Number.isInteger(metricValue) ? metricValue : Number(metricValue.toFixed(2));
  return `${rounded.toLocaleString()} ${controls.aggregate} ${controls.attribute}`;
};

export const buildContinuationSummaries = (
  items: Array<GraphNode | GraphLink>,
  entity: ScentEntity,
  controls: ScentControls,
  connectedNodeTypesByGroup: Map<string, Set<string>> = new Map()
): ContinuationSummary[] => {
  const typeKey = getTypeKey(entity);
  const groups = new Map<string, Array<GraphNode | GraphLink>>();

  items.forEach(item => {
    const groupValue = getGroupValue(item, controls.groupBy);
    if (!groups.has(groupValue)) {
      groups.set(groupValue, []);
    }
    groups.get(groupValue)!.push(item);
  });

  const summaries = Array.from(groups.entries()).map(([groupValue, groupItems]) => {
    const values = controls.aggregate === 'count' || controls.aggregate === 'runtime' || controls.aggregate === 'runtime_x1000' || controls.aggregate === 'fanout' || !controls.attribute
      ? groupItems.map(() => 1)
      : groupItems
        .map(item => (item as any)[controls.attribute!])
        .filter(isNumericValue);

    const metricValue = controls.aggregate === 'count' || controls.aggregate === 'runtime' || controls.aggregate === 'runtime_x1000' || controls.aggregate === 'fanout'
      ? groupItems.length
      : aggregateValues(values, controls);

    return {
      type: groupValue,
      id: groupValue,
      label: groupValue,
      count: groupItems.length,
      value: Math.max(metricValue, 0),
      metricValue,
      metricLabel: formatMetricLabel(metricValue, controls),
      groupBy: controls.groupBy,
      canNavigate: controls.groupBy === typeKey,
      examples: entity === 'nodes' ? groupItems.slice(0, 5) as GraphNode[] : undefined,
      connectedNodeTypes: entity === 'edges'
        ? Array.from(connectedNodeTypesByGroup.get(groupValue) || [])
        : undefined
    } as ContinuationSummary;
  });

  const hasPositiveLayoutValue = summaries.some(summary => summary.value > 0);
  if (!hasPositiveLayoutValue) {
    summaries.forEach(summary => {
      summary.value = summary.count;
    });
  }

  return summaries.sort((a, b) => {
    const labelA = a.label || a.type;
    const labelB = b.label || b.type;
    return b.value - a.value || b.count - a.count || labelA.localeCompare(labelB);
  });
};
